// 動態 chunk 載入失敗的復原。依「本地快取是否真的壞掉」分成兩類處置，不可混用：
//
// A 類（內容不對）：`Invalid or unexpected token`（CDN 回傳損壞但 200 的 JS 被 Workbox 快取後永久
//   重現，CLIENT-PWA-SSR-BD）、`does not provide an export named`（PWA 快取殘留舊 chunk，named
//   export 與其他 chunk 對不上；單一一致 build 內不可能發生，必為新舊 build 混用，
//   CLIENT-PWA-SSR-ZM）。壞的就是本地快取，必須清掉所有快取 + 反註冊 SW 才能復原。
//
// B 類（取檔失敗）：三種引擎措辭的 module fetch 失敗（CLIENT-PWA-SSR-1A9）。2026-08-09 以 Sentry
//   資料驗證：事件的 gitSha 一律等於當時線上的 sha（pool/bundle.txt），且事件中的失敗 chunk URL
//   逐一實抓皆仍回 200 —— 檔案好好地在 CDN 上，本地快取沒有壞，屬行動網路／CDN 的偶發取檔失敗。
//   此時清快取只會讓使用者在同一條劣化連線上重抓整包 precache，是淨傷害，故僅重整不清快取。
//   仍需重整的原因：依 HTML 規範，取檔失敗的 module URL 會在該文件的 module map 內被標記為失敗，
//   同一份文件內後續 import() 只會立即回傳同一個 rejection，重試無效，只有換一份文件才能復原。
//
// 兩類各自持有獨立的 sessionStorage guard（每個 session 至多復原一次，避免無限重載循環）。
// guard 必須分開：B 類事件量約為 A 類的 66 倍（30 天 3,725 vs 56），共用一個 key 會讓 B 類幾乎
// 必然先把額度用掉，導致真正需要清快取的 A 類永遠等不到復原。
import { usePwaUpdate } from '@/composables/core/usePwaUpdate'

const PURGE_GUARD_KEY = 'chunk-recovery-reloaded'
const RELOAD_GUARD_KEY = 'chunk-recovery-soft-reloaded'

const CORRUPT_CHUNK_RE = /Invalid or unexpected token|does not provide an export named/i
const FETCH_CHUNK_RE = /Importing a module script failed|Failed to fetch dynamically imported module|error loading dynamically imported module/i

export default defineNuxtPlugin((nuxtApp) => {
  // A 類：清空所有快取 + 反註冊 SW + 重整
  const purgeAndReload = async (reason) => {
    if (sessionStorage.getItem(PURGE_GUARD_KEY)) return
    sessionStorage.setItem(PURGE_GUARD_KEY, '1')
    console.error('[chunk-recovery] purge caches + reload:', reason)
    try {
      const { forceReload } = usePwaUpdate()
      await forceReload()
    } catch (error) {
      console.error('[chunk-recovery] forceReload failed, hard reloading:', error)
      window.location.reload()
    }
  }

  // B 類：只重整，保留快取與 SW（precache 健在時重整幾乎瞬間完成）
  const reloadOnly = (reason) => {
    if (sessionStorage.getItem(RELOAD_GUARD_KEY)) return
    sessionStorage.setItem(RELOAD_GUARD_KEY, '1')
    console.warn('[chunk-recovery] reload without purging caches:', reason)
    window.location.reload()
  }

  const recover = (error, source) => {
    if (!(error instanceof Error)) return
    const message = error.message || ''
    if (CORRUPT_CHUNK_RE.test(message)) {
      purgeAndReload(`${source}: ${message}`)
      return
    }
    if (FETCH_CHUNK_RE.test(message)) {
      reloadOnly(`${source}: ${message}`)
    }
  }

  // Vue 非同步元件載入器（runtime-13）拋出的錯誤 → BD 與 1A9 的多數事件走這條
  nuxtApp.hook('vue:error', (error) => {
    recover(error, 'vue:error')
  })

  // Vite preload 失敗（多為 fetch 失敗），縱深補強
  nuxtApp.hook('app:chunkError', ({ error }) => {
    recover(error, 'app:chunkError')
  })

  // Vue render 邊界外的動態 import()（事件處理器 / composable 內）失敗時，其 rejection
  // 不會經過上述兩個 hook，改由全域 unhandledrejection 接住。
  // 不呼叫 event.preventDefault()，讓 rejection 仍正常上報以利 triage（guard 達上限後不再重載）。
  const handleUnhandledRejection = (event) => {
    recover(event?.reason, 'unhandledrejection')
  }
  window.addEventListener('unhandledrejection', handleUnhandledRejection)
  nuxtApp.hook('app:unmount', () => {
    window.removeEventListener('unhandledrejection', handleUnhandledRejection)
  })
})
